import {
  IClientProfileUpdate,
  IItemWithProduct,
  IPatient,
  PaymentMethodChargeabilityStatus,
  UISubscriptionStatus,
} from "common/types";
import { Optional } from "common/utils/types";
import { Destination } from "common/router/types";
import { OnboardingClientEventKind } from "common/types/onboarding";
import {
  ControlledSubstanceClientProfileField,
  ControlledSubstanceComplianceFailureKind,
} from "common/types/controlledSubstances";
import { UnavailableReason } from "common/types/fulfillments";

import { ModalStatus } from "client/common/types";
import { IClient } from "client/accounts/types";

export type ControlledSubstancesRequiredField = Partial<keyof IClientProfileUpdate>;

/** matches onboarding ClientOnboardingSerializer */
export type OnboardingClient = {
  id: number;
  subscriptions: IOnboardingSubscription[];
  controlled_substances_required_fields: ControlledSubstancesRequiredField[];
  has_missing_controlled_substances_required_fields: boolean;
  controlled_substance_required_client_fields: ControlledSubstanceClientProfileField[];
  controlled_substance_client_resolvable_failures: OnboardingControlledSubstanceFailure[];
  has_controlled_substance: boolean;
};

/** matches onboarding ClientOnboardingControlledSubstanceFailureSerializer */
export type OnboardingControlledSubstanceFailure = {
  kind: ControlledSubstanceComplianceFailureKind;
  fields: ControlledSubstanceClientProfileField[];
  detail: string;
};

/** matches onboarding OnboardingSubscriptionPaymentMethodSerializer */
export type OnboardingSubscriptionPaymentMethod = {
  id: number;
  chargeability_status: PaymentMethodChargeabilityStatus;
  is_chargeable: boolean;
};

/* matches onboarding ClientSubscriptionOnboardingListSerializer */
export type IOnboardingSubscription = {
  id: number;
  item: IItemWithProduct;
  patient: IPatient;
  quantity: number;
  frequency_display: string;
  next_order_date: string | null;
  expected_delivery_by: string | null;
  visible_status: UISubscriptionStatus;
  visible_status_display: string;
  unit_price: number;
  subtotal: number;
  first_discount_value: number;
  recurring_discount_value: number;
  subtotal_with_first_discount: number;
  subtotal_with_recurring_discount: number;
  client_handling_fees: number;
  client_purchasing_fee: number;
  shipping: Optional<number>;
  total: number;
  payment_method: Optional<OnboardingSubscriptionPaymentMethod>;
  unavailable_reason: UnavailableReason | null;
};

/** Matches onboarding ClientCreateOnboardingEventSerializer  */
export interface OnboardingEventCreate {
  kind: OnboardingClientEventKind;
}

export type OnboradingSubscriptionsSearch = Record<string, string | boolean | number>;

export const OnboardingDetailSubscriptionStep = {
  QuantityFrequency: "quantity-frequency",
  NextOrderDate: "next-order-date",
  Cancel: "cancel",
  PatientMissingFields: "patient-missing-fields",
} as const;
export type OnboardingDetailSubscriptionStep =
  (typeof OnboardingDetailSubscriptionStep)[keyof typeof OnboardingDetailSubscriptionStep];

export const OnboardingSubscriptionsStep = {
  List: "list",
  QuantityFrequency: "quantity-frequency",
  NextOrderDate: "next-order-date",
  Cancel: "cancel",
  Billing: "billing",
  ProfileMissingFields: "profile-missing-fields",
  PatientMissingFields: "patient-missing-fields",
  ControlledSubstances: "controlled-substances",
  Complete: "complete",
} as const;
export type OnboardingSubscriptionsStep =
  (typeof OnboardingSubscriptionsStep)[keyof typeof OnboardingSubscriptionsStep];

export type OnboardingSubscriptionsStepParams = {
  id: Optional<number>;
  onNext: () => void;
  // whether the next button should be spinning as it tries to go to the next page
  isLoadingNext: boolean;
  client: IClient;
  onboarding: OnboardingClient;
  subscription: Optional<IOnboardingSubscription>;
  stepper: React.ReactNode;
  rootDest: Destination;
};

export type OnboardingDetailSubscriptionStepParams = OnboardingSubscriptionsStepParams & {
  id: number;
  subscription: IOnboardingSubscription;
};

export type OnboardingSubscriptionDetailParams = {
  id: number;
  step: OnboardingSubscriptionsStep;
};

export type OnboradingSubscriptionListParams = {
  step: OnboardingSubscriptionsStep;
};

export type OnboardingSubscriptionsBillingStepParams = {
  createAddressModalIsVisible: ModalStatus;
  createPaymentModalIsVisible: ModalStatus;
};

export type OnboardingSubscriptionNextOrderDateStepParams = {
  dateModalIsVisible: ModalStatus;
};

export type OnboardingSubscriptionsErrorSearch = {
  status?: number;
};
